import { useEffect, useState, useCallback } from 'react'
import { storage } from '@/lib/storage'

const DARK_MODE_PREFERENCE_KEY = 'ui.darkMode'

export function useDarkMode() {
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  // Load dark mode preference from IndexedDB on mount
  useEffect(() => {
    let cancelled = false

    async function loadDarkMode() {
      try {
        const savedPreference = await storage.getPreference(DARK_MODE_PREFERENCE_KEY)
        if (!cancelled) {
          const darkModeEnabled = savedPreference === true
          setIsDarkMode(darkModeEnabled)
          applyDarkModeToDocument(darkModeEnabled)
          setIsLoaded(true)
        }
      } catch (error) {
        console.error('Failed to load dark mode preference:', error)
        if (!cancelled) {
          setIsLoaded(true)
        }
      }
    }

    loadDarkMode()

    return () => {
      cancelled = true
    }
  }, [])

  // Apply dark mode class to document element
  const applyDarkModeToDocument = useCallback((enabled: boolean) => {
    if (enabled) {
      document.documentElement.setAttribute('data-appearance', 'dark')
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.removeAttribute('data-appearance')
      document.documentElement.classList.remove('dark')
    }
  }, [])

  // Toggle dark mode
  const toggleDarkMode = useCallback(async (enabled: boolean) => {
    setIsDarkMode(enabled)
    applyDarkModeToDocument(enabled)
    
    // Persist to IndexedDB
    try {
      await storage.savePreference(DARK_MODE_PREFERENCE_KEY, enabled)
    } catch (error) {
      console.error('Failed to save dark mode preference:', error)
    }
  }, [applyDarkModeToDocument])

  return {
    isDarkMode,
    toggleDarkMode,
    isLoaded
  }
}
